import { CheckCircle2, DatabaseBackup, Download, FileText, Loader2, Pencil, Play, Plus, Trash2, TriangleAlert, XCircle } from "lucide-react";
import { useState } from "react";
import { ApiRequestError, useApi } from "../api/client";
import { useDatabases, useFoundDatabases, useInvalidateOrg } from "../api/queries";
import type { DatabaseEngine, DatabaseInput, DatabaseInstance, FoundDatabase, Job, Snapshot } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { grantScript } from "../lib/dbgrants";
import { useFormat } from "../lib/format";
import { CodeBlock, ErrorState, LoadingRows } from "../ui/Common";
import { Dialog } from "../ui/Dialog";
import { Pill } from "../ui/Pills";
import { useToast } from "../ui/Toasts";
import { machineDatabasesMessages } from "./machineDatabases.i18n";

// Motores que o agente já sabe copiar; os demais aparecem como "em breve".
const SUPPORTED: DatabaseEngine[] = ["postgresql", "mysql", "sqlserver", "oracle"];
const ENGINES: DatabaseEngine[] = ["postgresql", "mysql", "sqlserver", "oracle", "firebird"];
const DEFAULT_PORT: Record<DatabaseEngine, number> = { postgresql: 5432, mysql: 3306, sqlserver: 1433, oracle: 1521, firebird: 3050 };
const DEFAULT_OS_USER: Partial<Record<DatabaseEngine, string>> = { postgresql: "postgres", mysql: "mysql", oracle: "oracle" };

type Editing = { instance: DatabaseInstance | null; from?: FoundDatabase };

/** Aba de bancos de dados da máquina (ADR 0023): cadastro, teste, backup na hora e download. */
export function MachineDatabases({ orgId, machineId, revoked, snapshots, jobs }: {
  orgId: string; machineId: string; revoked: boolean; snapshots: Snapshot[];
  /** Atividade da máquina: a mensagem do teste em curso é o checklist ao vivo (ADR 0025). */
  jobs: Job[];
}) {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const dbs = useDatabases(orgId, machineId);
  const found = useFoundDatabases(orgId, machineId);
  const t = useT(machineDatabasesMessages);
  const tc = useT(common);
  const f = useFormat();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [removing, setRemoving] = useState<DatabaseInstance | null>(null);
  const [busy, setBusy] = useState(false);

  if (dbs.isPending) return <LoadingRows rows={4} />;
  if (dbs.isError) return <div className="panel-body"><ErrorState error={dbs.error} /></div>;
  const instances = dbs.data;
  const known = new Set(instances.map((d) => d.id));
  // Motor já cadastrado (mesmo motor e mesma porta ou socket) não é oferecido de novo.
  const offers = (found.data?.found ?? []).filter((x) => !instances.some((d) => d.engine === x.engine &&
    (x.sid ? d.sid.toLowerCase() === x.sid.toLowerCase() : (x.socket && d.socket === x.socket) || (d.port || 0) === (x.port || 0))));
  const orphans = snapshots.filter((s) => !s.databaseId || !known.has(s.databaseId));

  async function run(inst: DatabaseInstance, kind: "test" | "backup") {
    setBusy(true);
    try {
      if (kind === "test") await api.testDatabase(orgId, machineId, inst.id);
      else await api.startDatabaseBackup(orgId, machineId, inst.id);
      toast("ok", kind === "test" ? t.testSent(inst.name) : t.backupSent(inst.name));
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.actionFailed);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await api.deleteDatabase(orgId, machineId, removing.id);
      toast("ok", t.removed(removing.name));
      setRemoving(null);
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.removeFailed);
    } finally {
      setBusy(false);
    }
  }

  function download(snapshotId: string, stream: string) {
    // O servidor manda o dump como saiu da ferramenta; o navegador grava direto em disco.
    window.location.assign(`/api/v1/orgs/${encodeURIComponent(orgId)}/snapshots/${encodeURIComponent(snapshotId)}/databases/${encodeURIComponent(stream)}`);
    toast("ok", t.downloadStarted);
  }

  const engineName = (e: string) => (t.engine as Record<string, string>)[e] ?? e;

  function backupsTable(points: Snapshot[]) {
    if (points.length === 0) return <p className="muted" style={{ fontSize: "var(--fs-13)" }}>{t.noBackups}</p>;
    return (
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>{t.colWhen}</th>
              <th>{t.colDatabase}</th>
              <th className="right">{t.colSize}</th>
              <th><span className="sr-only">{t.colActions}</span></th>
            </tr>
          </thead>
          <tbody>
            {points.flatMap((s) => (s.databases.length ? s.databases : [null]).map((d, i) => (
              <tr key={`${s.id}-${d?.stream ?? i}`}>
                {i === 0 ? (
                  <td rowSpan={Math.max(s.databases.length, 1)}>
                    <div className="host">
                      <strong style={{ fontFamily: "var(--font-ui)", fontWeight: 600 }}>{t.when(f.weekdayDate(s.startedAt), f.time(s.startedAt))}</strong>
                      <span className="mono">{s.id.slice(0, 12)} · {s.trigger === "manual" ? t.triggerManual : t.triggerSchedule} · {f.bytes(s.totalBytes)}</span>
                      <button className="btn btn-sm" type="button" style={{ marginTop: 6, justifySelf: "start" }} title={t.manifestHint} onClick={() => download(s.id, "manifest.json")}>
                        <FileText aria-hidden="true" /> {t.manifest}
                      </button>
                    </div>
                  </td>
                ) : null}
                <td className="mono">{d ? (d.name || t.globals) : "—"}{d?.format ? <span className="muted"> · {d.format}</span> : null}</td>
                <td className="right num">{d ? f.bytes(d.sizeBytes) : "—"}</td>
                <td className="right">
                  {d && (
                    <button className="btn btn-sm" type="button" onClick={() => download(s.id, d.stream)}>
                      <Download aria-hidden="true" /> {t.download}
                    </button>
                  )}
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="panel-body" style={{ display: "grid", gap: 20 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
        <p className="ink-2" style={{ fontSize: "var(--fs-13)", maxWidth: "62ch" }}>{t.intro}</p>
        {!revoked && (
          <button className="btn" type="button" onClick={() => setEditing({ instance: null })}>
            <Plus aria-hidden="true" /> {t.add}
          </button>
        )}
      </div>

      {!revoked && offers.length > 0 && (
        <section aria-labelledby="bancos-achados" style={{ display: "grid", gap: 8 }}>
          <h3 id="bancos-achados" style={{ fontSize: "var(--fs-13)" }}>{t.foundTitle}</h3>
          <p className="muted" style={{ fontSize: "var(--fs-12)" }}>{t.foundHint}</p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {offers.map((x, i) => (
              <li key={`${x.engine}-${x.port ?? 0}-${x.socket ?? ""}-${i}`} style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                <span style={{ minWidth: 0 }}>
                  <strong>{x.flavor === "mariadb" ? "MariaDB" : engineName(x.engine)}{x.version ? ` ${x.version}` : ""}{x.sid ? ` · ${x.sid}` : ""}</strong>{" "}
                  <span className="mono muted" style={{ overflowWrap: "anywhere" }}>
                    {[x.socket ? t.connSocket(x.socket) : x.port ? `:${x.port}` : "", x.detail ? t.foundWhere(x.detail) : ""].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {SUPPORTED.includes(x.engine as DatabaseEngine)
                  ? <button className="btn btn-sm" type="button" onClick={() => setEditing({ instance: null, from: x })}>{t.register}</button>
                  : <span className="tag">{t.engineSoon(engineName(x.engine))}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {instances.length === 0 && orphans.length === 0 && (
        <div className="empty"><DatabaseBackup aria-hidden="true" />{t.none}</div>
      )}

      {instances.map((d) => {
        const points = snapshots.filter((s) => s.databaseId === d.id);
        const conn = d.engine === "oracle" ? t.oracleSummary(d.sid, d.mode === "rman" ? "RMAN" : "Data Pump")
          : d.engine === "sqlserver" ? t.sqlserverSummary(d.sid)
          : d.socket ? t.connSocket(d.socket) : d.host ? `${d.host}${d.port ? `:${d.port}` : ""}` : t.connLocal;
        const auth = d.auth === "os" ? t.authOs(d.osUser) : t.authPassword(d.user);
        const what = d.all ? t.allDatabases : t.someDatabases(d.databases.length);
        const sched = d.schedule ? (d.schedule.frequency === "hourly" ? t.scheduleHourly : t.scheduleOwn(d.schedule.time)) : t.scheduleFromPolicy;
        const lt = d.lastTest;
        const live = d.activeJob ? jobs.find((j) => j.databaseId === d.id && (j.status === "running" || j.status === "queued")) : undefined;
        return (
          <section key={d.id} aria-labelledby={`banco-${d.id}`} style={{ display: "grid", gap: 12, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
              <div style={{ minWidth: 0, display: "grid", gap: 4 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <h3 id={`banco-${d.id}`} style={{ fontSize: "var(--fs-15, 0.95rem)" }}>{d.name}</h3>
                  <span className="tag">{engineName(d.engine)}</span>
                  {!d.enabled && <span className="tag" title={t.disabledHint}>{t.disabled}</span>}
                </div>
                <span className="mono ink-2" style={{ fontSize: "var(--fs-12)", overflowWrap: "anywhere" }}>{[conn, auth, what, sched].join(" · ")}</span>
              </div>
              {!revoked && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button className="btn btn-sm" type="button" disabled={busy || d.activeJob !== ""} onClick={() => run(d, "test")}>
                    <CheckCircle2 aria-hidden="true" /> {d.activeJob === "dbtest" ? t.testing : t.test}
                  </button>
                  <button className="btn btn-sm" type="button" disabled={busy || d.activeJob !== ""} onClick={() => run(d, "backup")}>
                    <Play aria-hidden="true" /> {d.activeJob === "database" ? t.backingUp : t.backupNow}
                  </button>
                  <button className="btn btn-sm" type="button" onClick={() => setEditing({ instance: d })} aria-label={`${t.edit}: ${d.name}`}>
                    <Pencil aria-hidden="true" /> {t.edit}
                  </button>
                  <button className="btn btn-sm" type="button" onClick={() => setRemoving(d)} aria-label={`${t.remove}: ${d.name}`}>
                    <Trash2 aria-hidden="true" /> {t.remove}
                  </button>
                </div>
              )}
            </div>

            {live && (
              <div className="callout" role="status" aria-live="polite">
                <Loader2 aria-hidden="true" className="spin" />
                <span><strong>{d.activeJob === "dbtest" ? t.testing : t.backingUp}</strong> {live.message}</span>
              </div>
            )}

            {lt ? (
              <div className={`callout ${lt.ok ? (lt.warnings.length ? "warn" : "") : "crit"}`} role="status">
                {lt.ok ? (lt.warnings.length ? <TriangleAlert aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />) : <XCircle aria-hidden="true" />}
                <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
                  <span><strong>{lt.ok ? t.testOk(f.ago(lt.at)) : t.testFailed(f.ago(lt.at))}.</strong> {lt.ok ? "" : lt.message}</span>
                  {lt.version && <span className="mono" style={{ fontSize: "var(--fs-12)" }}>{[t.serverInfo(lt.version, lt.tool, lt.toolVersion), lt.role ? (t.role[lt.role] ?? lt.role) : ""].filter(Boolean).join(" · ")}</span>}
                  {lt.databases.length > 0 && (
                    <span style={{ fontSize: "var(--fs-12)", overflowWrap: "anywhere" }}>
                      {t.seenDatabases(lt.databases.map((x) => (x.sizeBytes ? `${x.name} (${f.bytes(x.sizeBytes)})` : x.name)).join(", "))}
                    </span>
                  )}
                  {/* Checklist (ADR 0025): cada passo com o resultado. Os avisos
                      já entram como passos; a lista solta é para agente antigo. */}
                  {lt.steps.length > 0 ? (
                    <ul aria-label={t.testSteps} style={{ listStyle: "none", padding: 0, margin: "4px 0 0", display: "grid", gap: 2, fontSize: "var(--fs-12)" }}>
                      {lt.steps.map((st, i) => (
                        <li key={`${st.code}-${i}`} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                          {st.status === "ok" ? <CheckCircle2 aria-label={t.stepStatus.ok} style={{ width: 14, height: 14, flex: "none", marginTop: 1, color: "var(--ok-mark)" }} />
                            : st.status === "warn" ? <TriangleAlert aria-label={t.stepStatus.warn} style={{ width: 14, height: 14, flex: "none", marginTop: 1, color: "var(--warn-mark)" }} />
                            : <XCircle aria-label={t.stepStatus.fail} style={{ width: 14, height: 14, flex: "none", marginTop: 1, color: "var(--crit-mark)" }} />}
                          <span style={{ overflowWrap: "anywhere" }}>{st.message}</span>
                        </li>
                      ))}
                    </ul>
                  ) : lt.warnings.map((w) => <span key={w.code + w.message}>{w.message}</span>)}
                </div>
              </div>
            ) : !live ? (
              <div><Pill tone="idle">{t.neverTested}</Pill></div>
            ) : null}

            {backupsTable(points)}
          </section>
        );
      })}

      {orphans.length > 0 && (
        <section style={{ display: "grid", gap: 12, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
          <h3 style={{ fontSize: "var(--fs-15, 0.95rem)" }}>{t.removedInstance(engineName(orphans[0]?.sourcePath.split("/")[2] ?? ""))}</h3>
          {backupsTable(orphans)}
        </section>
      )}

      {editing && (
        <DatabaseDialog orgId={orgId} machineId={machineId} editing={editing} onClose={(changed) => { setEditing(null); if (changed) invalidate(orgId); }} />
      )}

      <Dialog
        open={removing !== null}
        onOpenChange={(v) => { if (!v) setRemoving(null); }}
        title={removing ? t.removeTitle(removing.name) : ""}
        description={t.removeDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setRemoving(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={remove} disabled={busy}>{t.remove}</button>
          </>
        }
      />
    </div>
  );
}

function DatabaseDialog({ orgId, machineId, editing, onClose }: { orgId: string; machineId: string; editing: Editing; onClose: (changed: boolean) => void }) {
  const api = useApi();
  const toast = useToast();
  const t = useT(machineDatabasesMessages);
  const tc = useT(common);
  const cur = editing.instance;
  const from = editing.from;
  const startEngine: DatabaseEngine = cur?.engine ?? (from && SUPPORTED.includes(from.engine as DatabaseEngine) ? (from.engine as DatabaseEngine) : "postgresql");

  const [name, setName] = useState(cur?.name ?? "");
  const [engine, setEngine] = useState<DatabaseEngine>(startEngine);
  const [host, setHost] = useState(cur?.host ?? from?.host ?? "");
  const [port, setPort] = useState(String(cur?.port || from?.port || DEFAULT_PORT[startEngine]));
  const [socket, setSocket] = useState(cur?.socket ?? from?.socket ?? "");
  const [auth, setAuth] = useState<"os" | "password">(cur?.auth ?? "os");
  const [osUser, setOsUser] = useState(cur?.osUser ?? from?.osUser ?? DEFAULT_OS_USER[startEngine] ?? "");
  const [user, setUser] = useState(cur?.user ?? "");
  const [password, setPassword] = useState("");
  const [all, setAll] = useState(cur?.all ?? true);
  const [list, setList] = useState((cur?.databases ?? []).join("\n"));
  const [ownSchedule, setOwnSchedule] = useState(Boolean(cur?.schedule));
  const [time, setTime] = useState(cur?.schedule?.time ?? "01:00");
  const [windowHours, setWindowHours] = useState(cur?.schedule?.windowHours ?? 4);
  const [weekdays, setWeekdays] = useState<number[]>(cur?.schedule?.weekdays ?? [0, 1, 2, 3, 4, 5, 6]);
  const [enabled, setEnabled] = useState(cur?.enabled ?? true);
  // Oracle (ADR 0025): instância, modo, área de preparação e archived logs.
  const [sid, setSid] = useState(cur?.sid ?? from?.sid ?? "");
  const [mode, setMode] = useState<"datapump" | "rman">(cur?.mode === "rman" ? "rman" : "datapump");
  const [stagingDir, setStagingDir] = useState(cur?.stagingDir ?? "");
  const [deleteArchivedLogs, setDeleteArchivedLogs] = useState(cur?.deleteArchivedLogs ?? false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const oracle = engine === "oracle";
  // SQL Server (ADR 0031): instância nomeada opcional e área de preparação; sem socket nem usuário do sistema.
  const sqlserver = engine === "sqlserver";
  const staged = oracle || sqlserver;

  const names = list.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
  const keepsSecret = Boolean(cur?.hasSecret) && cur?.auth === "password";
  // Script de criação do usuário, com o que o formulário já sabe; a senha fica como <senha>.
  const grants = grantScript(engine, { user, host, mode, databases: names, all, integrated: sqlserver && auth === "os" });
  const errors = {
    name: !name.trim() ? t.nameRequired : null,
    sid: oracle && !/^[A-Za-z][A-Za-z0-9_$#]{0,15}$/.test(sid.trim()) ? t.sidRequired
      : sqlserver && sid.trim() && !/^[A-Za-z][A-Za-z0-9_$#]{0,15}$/.test(sid.trim()) ? t.instanceInvalid : null,
    databases: !all && names.length === 0 && !oracle ? t.databasesRequired : null,
    user: auth === "password" && !user.trim() ? t.userRequired : null,
    password: auth === "password" && !password && !keepsSecret ? t.passwordRequired : null,
  };
  const show = (k: keyof typeof errors) => (touched ? errors[k] : null);

  function pickEngine(e: DatabaseEngine) {
    // Trocar o motor troca os padrões que ainda não foram mexidos.
    if (port === String(DEFAULT_PORT[engine])) setPort(String(DEFAULT_PORT[e]));
    if (osUser === (DEFAULT_OS_USER[engine] ?? "")) setOsUser(DEFAULT_OS_USER[e] ?? "");
    setEngine(e);
  }

  async function save(ev: React.FormEvent) {
    ev.preventDefault();
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    const input: DatabaseInput = {
      name: name.trim(), engine, host: host.trim(), port: Number(port) || 0, socket: socket.trim(), auth,
      user: auth === "password" ? user.trim() : "", osUser: auth === "os" ? osUser.trim() : "",
      databases: all ? [] : names, all, enabled,
      schedule: ownSchedule ? { frequency: "daily", time, weekdays: [...weekdays].sort(), windowHours: Math.min(24, Math.max(1, windowHours)) } : null,
      ...(oracle ? { sid: sid.trim(), mode, stagingDir: stagingDir.trim(), deleteArchivedLogs: mode === "rman" && deleteArchivedLogs } : {}),
      ...(sqlserver ? { sid: sid.trim(), stagingDir: stagingDir.trim() } : {}),
      // Ausente mantém a senha guardada; só vai quando a pessoa digitou uma.
      ...(auth === "password" && password ? { password } : {}),
    };
    setBusy(true);
    try {
      const saved = cur ? await api.updateDatabase(orgId, machineId, cur.id, input) : await api.createDatabase(orgId, machineId, input);
      toast("ok", cur ? t.saved(saved.name) : t.created(saved.name));
      onClose(true);
    } catch (err) {
      toast("error", err instanceof ApiRequestError ? err.message : t.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(false); }}
      title={cur ? t.editTitle(cur.name) : t.createTitle}
      footer={
        <>
          <button className="btn" type="button" onClick={() => onClose(false)}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="banco-form" disabled={busy}>{cur ? t.save : t.create}</button>
        </>
      }
    >
      <form id="banco-form" onSubmit={save} style={{ display: "grid", gap: 16 }} noValidate>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="banco-nome">{t.fieldName}</label>
            <input id="banco-nome" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder={t.namePlaceholder} aria-invalid={Boolean(show("name"))} />
            {show("name") && <span className="error">{show("name")}</span>}
          </div>
          <div className="field">
            <label htmlFor="banco-motor">{t.fieldEngine}</label>
            <select id="banco-motor" className="select" value={engine} disabled={Boolean(cur)} onChange={(e) => pickEngine(e.target.value as DatabaseEngine)}>
              {ENGINES.map((e) => (
                <option key={e} value={e} disabled={!SUPPORTED.includes(e)}>{SUPPORTED.includes(e) ? t.engine[e] : t.engineSoon(t.engine[e])}</option>
              ))}
            </select>
            {cur && <span className="hint">{t.engineFixed}</span>}
          </div>
        </div>

        {oracle && (
          <>
            <p className="callout" style={{ margin: 0 }}>{t.oracleSameMachine}</p>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="banco-sid">{t.fieldSid}</label>
                <input id="banco-sid" className="input mono" value={sid} onChange={(e) => setSid(e.target.value)} placeholder="ORCL" autoComplete="off" spellCheck={false} aria-invalid={Boolean(show("sid"))} />
                {show("sid") ? <span className="error">{show("sid")}</span> : <span className="hint">{t.sidHint}</span>}
              </div>
              <div className="field">
                <span className="label" id="banco-modo">{t.fieldMode}</span>
                <div className="segmented" role="group" aria-labelledby="banco-modo">
                  <button type="button" aria-pressed={mode === "datapump"} onClick={() => setMode("datapump")}>Data Pump</button>
                  <button type="button" aria-pressed={mode === "rman"} onClick={() => setMode("rman")}>RMAN</button>
                </div>
                <span className="hint">{mode === "rman" ? t.modeRmanHint : t.modeDataPumpHint}</span>
              </div>
            </div>
            <div className="field">
              <label htmlFor="banco-area">{t.fieldStaging}</label>
              <input id="banco-area" className="input mono" value={stagingDir} onChange={(e) => setStagingDir(e.target.value)} placeholder="/u01/backup" autoComplete="off" spellCheck={false} />
              <span className="hint">{t.stagingHint}</span>
            </div>
            {mode === "rman" && (
              <label className="check" htmlFor="banco-archived">
                <input id="banco-archived" type="checkbox" checked={deleteArchivedLogs} onChange={(e) => setDeleteArchivedLogs(e.target.checked)} />
                <span><strong>{t.deleteArchivedLogs}</strong><br /><span className="ink-2">{t.deleteArchivedLogsHint}</span></span>
              </label>
            )}
          </>
        )}

        {sqlserver && (
          <>
            <p className="callout" style={{ margin: 0 }}>{t.sqlserverSameMachine}</p>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="banco-instancia">{t.fieldInstance}</label>
                <input id="banco-instancia" className="input mono" value={sid} onChange={(e) => setSid(e.target.value)} placeholder="SQLEXPRESS" autoComplete="off" spellCheck={false} aria-invalid={Boolean(show("sid"))} />
                {show("sid") ? <span className="error">{show("sid")}</span> : <span className="hint">{t.instanceHint}</span>}
              </div>
              <div className="field">
                <label htmlFor="banco-area">{t.fieldStaging}</label>
                <input id="banco-area" className="input mono" value={stagingDir} onChange={(e) => setStagingDir(e.target.value)} placeholder="D:\Backup" autoComplete="off" spellCheck={false} />
                <span className="hint">{t.stagingHintSqlserver}</span>
              </div>
            </div>
          </>
        )}

        {(!oracle || auth === "password") && (
        <div className="form-grid">
          <div className="field">
            <label htmlFor="banco-host">{t.fieldHost}</label>
            <input id="banco-host" className="input mono" value={host} onChange={(e) => setHost(e.target.value)} placeholder="127.0.0.1" autoComplete="off" spellCheck={false} />
            <span className="hint">{t.hostHint}</span>
          </div>
          <div className="field">
            <label htmlFor="banco-porta">{t.fieldPort}</label>
            <input id="banco-porta" className="input num" type="number" min={0} max={65535} value={port} onChange={(e) => setPort(e.target.value)} />
          </div>
        </div>
        )}
        {!staged && (
          <div className="field">
            <label htmlFor="banco-socket">{t.fieldSocket}</label>
            <input id="banco-socket" className="input mono" value={socket} onChange={(e) => setSocket(e.target.value)} placeholder="/var/run/postgresql" autoComplete="off" spellCheck={false} />
            <span className="hint">{t.socketHint}</span>
          </div>
        )}

        <div className="field">
          <span className="label" id="banco-auth">{t.fieldAuth}</span>
          <div className="segmented" role="group" aria-labelledby="banco-auth">
            <button type="button" aria-pressed={auth === "os"} onClick={() => setAuth("os")}>{t.authOsOption}</button>
            <button type="button" aria-pressed={auth === "password"} onClick={() => setAuth("password")}>{t.authPasswordOption}</button>
          </div>
          <span className="hint">{auth === "os" ? (oracle ? t.authOsOracleHint : sqlserver ? t.authOsSqlserverHint : t.authOsHint) : (oracle ? t.authPasswordOracleHint : sqlserver ? t.authPasswordSqlserverHint : t.authPasswordHint)}</span>
        </div>
        {auth === "os" ? (!staged && (
          <div className="field">
            <label htmlFor="banco-osuser">{t.fieldOsUser}</label>
            <input id="banco-osuser" className="input mono" value={osUser} onChange={(e) => setOsUser(e.target.value)} autoComplete="off" spellCheck={false} />
            <span className="hint">{t.osUserHint}</span>
          </div>
        )) : (
          <div className="form-grid">
            <div className="field">
              <label htmlFor="banco-usuario">{t.fieldUser}</label>
              <input id="banco-usuario" className="input mono" value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" spellCheck={false} aria-invalid={Boolean(show("user"))} />
              {show("user") && <span className="error">{show("user")}</span>}
            </div>
            <div className="field">
              <label htmlFor="banco-senha">{t.fieldPassword}</label>
              <input id="banco-senha" className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" aria-invalid={Boolean(show("password"))} />
              {show("password") ? <span className="error">{show("password")}</span> : keepsSecret ? <span className="hint">{t.passwordKeep}</span> : null}
            </div>
          </div>
        )}
        {(auth === "password" || sqlserver) && grants && (
          <details className="grants">
            <summary>{t.grantsTitle}</summary>
            <p className="hint">{sqlserver && auth === "os" ? t.grantsSqlserverIntegrated : t.grantsHint}</p>
            <CodeBlock code={grants} label={t.grantsLabel} />
            <p className="hint">{engine === "postgresql" ? t.grantsPostgres : engine === "mysql" ? t.grantsMysql : engine === "sqlserver" ? t.grantsSqlserver : t.grantsOracle}</p>
          </details>
        )}

        {!(oracle && mode === "rman") && (
          <div className="field">
            <span className="label" id="banco-oque">{oracle ? t.fieldWhatPdbs : t.fieldWhat}</span>
            <div className="segmented" role="group" aria-labelledby="banco-oque">
              <button type="button" aria-pressed={all} onClick={() => setAll(true)}>{oracle ? t.whatAllPdbs : t.whatAll}</button>
              <button type="button" aria-pressed={!all} onClick={() => setAll(false)}>{t.whatList}</button>
            </div>
          </div>
        )}
        {!all && !(oracle && mode === "rman") && (
          <div className="field">
            <label htmlFor="banco-lista">{oracle ? t.fieldPdbs : t.fieldDatabases}</label>
            <textarea id="banco-lista" className="input mono" rows={3} value={list} onChange={(e) => setList(e.target.value)} spellCheck={false} aria-invalid={Boolean(show("databases"))} />
            {show("databases") ? <span className="error">{show("databases")}</span> : <span className="hint">{t.databasesHint}</span>}
          </div>
        )}

        <div className="field">
          <span className="label" id="banco-agenda">{t.fieldSchedule}</span>
          <div className="segmented" role="group" aria-labelledby="banco-agenda">
            <button type="button" aria-pressed={!ownSchedule} onClick={() => setOwnSchedule(false)}>{t.schedulePolicy}</button>
            <button type="button" aria-pressed={ownSchedule} onClick={() => setOwnSchedule(true)}>{t.scheduleCustom}</button>
          </div>
        </div>
        {ownSchedule && (
          <>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="banco-hora">{t.fieldTime}</label>
                <input id="banco-hora" className="input num" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="banco-janela">{t.fieldWindow}</label>
                <input id="banco-janela" className="input num" type="number" min={1} max={24} value={windowHours} onChange={(e) => setWindowHours(Number(e.target.value))} />
              </div>
            </div>
            <div className="field">
              <span className="label" id="banco-dias">{t.fieldDays}</span>
              <div className="segmented" role="group" aria-labelledby="banco-dias">
                {tc.weekdaysShort.map((d, i) => {
                  const on = weekdays.includes(i);
                  return <button key={d} type="button" aria-pressed={on} onClick={() => setWeekdays(on ? weekdays.filter((x) => x !== i) : [...weekdays, i])}>{d}</button>;
                })}
              </div>
            </div>
          </>
        )}

        <label className="check" htmlFor="banco-ligada">
          <input id="banco-ligada" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span><strong>{t.enabled}</strong><br /><span className="ink-2">{t.disabledHint}</span></span>
        </label>
      </form>
    </Dialog>
  );
}
